/**
 * DU schedule is fetched from your own backend/Worker:
 *   GET /api/du/schedule?group=GROUP_NAME
 *
 * Frontend DOES NOT send any secrets (no X-ICS-KEY, no DU token, nothing).
 * All secrets must live on the server (Cloudflare Worker secrets / KV / D1).
 */

import { normalizeDuLessons } from "./duNormalize";

const DU_API_PATH = "/api/du/schedule";

// localStorage key
const DU_CACHE_KEY = "du_schedule_cache_term_v1";

type DuCacheEntry = {
  term: string;
  at: number;
  group: string;
  data: any;
};

/**
 * Returns current term key.
 * If outside known periods, returns OUT-<year>.
 *
 * Your rule:
 * - Sep 1 — Nov 30
 * - Dec 1 — Feb 27
 * - Mar 1 — May 30
 */
function currentDuTermKey(now = new Date()): string {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;

  // Sep-Nov
  if (m === 9 || m === 10 || m === 11) {
    return `${y}-09_${y}-11`;
  }

  // Dec-Feb (until Feb 27)
  if (m === 12) {
    return `${y}-12_${y + 1}-02-27`;
  }
  if (m === 1 || m === 2) {
    const startY = y - 1;
    return `${startY}-12_${y}-02-27`;
  }

  // Mar-May
  if (m === 3 || m === 4 || m === 5) {
    return `${y}-03_${y}-05`;
  }

  return `OUT-${y}`;
}

function loadDuCacheAll(): Record<string, DuCacheEntry> {
  try {
    const raw = localStorage.getItem(DU_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, DuCacheEntry>) : {};
  } catch {
    return {};
  }
}

function saveDuCacheAll(obj: Record<string, DuCacheEntry>) {
  try {
    localStorage.setItem(DU_CACHE_KEY, JSON.stringify(obj));
  } catch {
    // ignore
  }
}

function cacheKey(group: string, term: string) {
  return `${group}__${term}`;
}

/** Load cache for current term only */
function loadCachedCurrentTerm(group: string): DuCacheEntry | null {
  const term = currentDuTermKey(new Date());
  const all = loadDuCacheAll();
  const entry = all[cacheKey(group, term)];
  if (!entry) return null;
  if (entry.term !== term) return null;
  return entry;
}

/** Load newest cache entry for this group for ANY term (fallback) */
function loadAnyCached(group: string): DuCacheEntry | null {
  const all = loadDuCacheAll();
  const prefix = `${group}__`;

  const entries = Object.entries(all)
    .filter(([k, v]) => k.startsWith(prefix) && !!v)
    .map(([, v]) => v)
    .sort((a, b) => (b?.at || 0) - (a?.at || 0));

  return entries[0] || null;
}

function saveCached(group: string, data: any) {
  const term = currentDuTermKey(new Date());
  const all = loadDuCacheAll();

  all[cacheKey(group, term)] = {
    term,
    at: Date.now(),
    group,
    data,
  };

  // keep last 12 entries globally
  const keys = Object.keys(all);
  if (keys.length > 12) {
    keys
      .sort((a, b) => (all[a]?.at || 0) - (all[b]?.at || 0))
      .slice(0, keys.length - 12)
      .forEach((k) => delete all[k]);
  }

  saveDuCacheAll(all);
}

export function clearDuCacheForCurrentTerm(group?: string) {
  const term = currentDuTermKey(new Date());
  const all = loadDuCacheAll();

  if (group) {
    delete all[cacheKey(group, term)];
  } else {
    for (const k of Object.keys(all)) {
      if (k.endsWith(`__${term}`)) delete all[k];
    }
  }

  saveDuCacheAll(all);
}

function normalizeGroup(input: string) {
  return (input || "")
    .trim()
    // some devices may produce broken dash chars; normalize them
    .replace(/[��???]/g, "-");
}

async function readTextSafe(r: Response) {
  try {
    return await r.text();
  } catch {
    return "";
  }
}

/**
 * Fetch DU schedule from your own backend/worker.
 * - Uses current-term cache first
 * - On error/offline, falls back to newest cached value (any term)
 */
export async function fetchDuSchedule(group: string) {
  const g = normalizeGroup(group);
  if (!g) throw new Error("group is empty");

  // 1) Current term cache
  const cached = loadCachedCurrentTerm(g);
  if (cached) return normalizeDuLessons(cached.data);

  const url = `${DU_API_PATH}?group=${encodeURIComponent(g)}`;

  let r: Response;
  let text = "";

  try {
    r = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      // credentials: "include" // (optional) if your backend uses cookies/auth
    });
    text = await readTextSafe(r);
  } catch (e) {
    // network/offline -> fallback to any cached
    const any = loadAnyCached(g);
    if (any) return normalizeDuLessons(any.data);
    throw e;
  }

  if (!r.ok) {
    // server/du failure -> fallback to any cached
    const any = loadAnyCached(g);
    if (any) return normalizeDuLessons(any.data);

    throw new Error(`DU api error ${r.status}: ${text}`);
  }

  if (text.trim().startsWith("<")) {
    // backend returned HTML (edge error page etc.)
    const any = loadAnyCached(g);
    if (any) return normalizeDuLessons(any.data);

    throw new Error(`DU api returned HTML, not JSON: ${text.slice(0, 160)}`);
  }

  try {
    const json = JSON.parse(text);
    saveCached(g, json);
    return normalizeDuLessons(json);
  } catch {
    const any = loadAnyCached(g);
    if (any) return normalizeDuLessons(any.data);

    throw new Error(`DU api returned invalid JSON: ${text.slice(0, 200)}`);
  }
}
